import { Badge } from "@bcc/components/ui/badge";
import { Button } from "@bcc/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@bcc/components/ui/card";
import { Download, Printer } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  setRecentLabelsTestId,
  setRecentLabelsTitleTestId,
} from "./recent-labels.utils";

interface RecentLabelData {
  id: number;
  product: string;
  template: string;
  quantity: number;
  date: string;
  status: "printed" | "ready";
}

interface RecentLabelsProps {
  data: RecentLabelData[];
  testId?: string;
}

interface RecentLabelItemProps {
  label: RecentLabelData;
  onDownload: () => void;
  onPrint: () => void;
  index: number;
}

function RecentLabelItem({
  label,
  onDownload,
  onPrint,
  index,
}: RecentLabelItemProps) {
  const { t } = useTranslation();

  const statusConfig = {
    printed: {
      label: t("views.recentLabels.status.printed"),
      className: "bg-primary/10 text-primary hover:bg-primary/20",
    },
    ready: {
      label: t("views.recentLabels.status.ready"),
      className:
        "bg-amber-100 text-amber-700 hover:bg-amber-200 dark:bg-amber-950 dark:text-amber-400",
    },
  };

  return (
    <div
      className="flex items-center justify-between rounded-lg border border-border p-4"
      data-testid={setRecentLabelsTestId(`item-${index}`)}
    >
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <p
            className="font-medium"
            data-testid={setRecentLabelsTestId(`product-${index}`)}
          >
            {label.product}
          </p>
          <Badge
            variant="secondary"
            className={statusConfig[label.status].className}
            data-testid={setRecentLabelsTestId(`status-${index}`)}
          >
            {statusConfig[label.status].label}
          </Badge>
        </div>
        <p
          className="text-sm text-muted-foreground mt-1"
          data-testid={setRecentLabelsTestId(`details-${index}`)}
        >
          {label.template} •{" "}
          {t("views.recentLabels.labelsCount", { count: label.quantity })} •{" "}
          {label.date}
        </p>
      </div>
      <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" onClick={onDownload}>
          <Download className="h-4 w-4" />
        </Button>
        <Button variant="outline" size="sm" onClick={onPrint}>
          <Printer className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

export function RecentLabels({ data, testId }: RecentLabelsProps) {
  const { t } = useTranslation();

  const handleDownload = (labelId: number) => {
    console.log("Download label:", labelId);
  };

  const handlePrint = (labelId: number) => {
    console.log("Print label:", labelId);
  };

  return (
    <Card testId={testId ?? setRecentLabelsTestId("container")}>
      <CardHeader>
        <CardTitle data-testid={setRecentLabelsTitleTestId()}>
          {t("views.recentLabels.title")}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {data.map((label, index) => (
            <RecentLabelItem
              key={label.id}
              label={label}
              onDownload={() => handleDownload(label.id)}
              onPrint={() => handlePrint(label.id)}
              index={index}
            />
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
